import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { apiClient } from '../lib/api';
import { PageLoader } from '@lex/ui';
import type { CaseStage } from '@lex/contracts';

const STAGE_LABEL: Record<CaseStage, string> = {
  accepted: 'Принято',
  documents: 'Документы',
  draft: 'Черновик',
  filed: 'Подано',
  response: 'Ответ',
  completed: 'Завершено',
};

const STAGE_CLASS: Record<CaseStage, string> = {
  accepted: 'bg-accent-soft text-accent',
  documents: 'bg-[#FBF3DE] text-[#7A5C14]',
  draft: 'bg-accent-soft text-accent',
  filed: 'bg-success-soft text-success',
  response: 'bg-success-soft text-success',
  completed: 'bg-soft text-muted',
};

function dateLabel(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('ru-RU', {
    timeZone: 'Europe/Moscow',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
    .format(new Date(iso))
    .replace(' г.', '');
}

export function CasesPage() {
  const cases = useQuery({ queryKey: ['cases'], queryFn: () => apiClient.chat.myCases() });

  if (cases.isLoading) return <PageLoader />;

  return (
    <div className="mx-auto max-w-5xl space-y-5 pb-10">
      <div>
        <Link to="/cabinet" className="text-[14px] font-semibold text-accent">
          ← В кабинет
        </Link>
        <h1 className="mt-3 text-[34px] font-semibold leading-tight tracking-[-0.02em] text-ink md:text-[44px]">
          Дела
        </h1>
        <p className="mt-2 text-[15px] text-muted">
          Этапы работы, сроки, документы и черновики по вашим обращениям.
        </p>
      </div>

      {(cases.data?.length ?? 0) === 0 ? (
        <div className="rounded-3xl border border-line bg-white/90 p-8 text-center">
          <p className="font-semibold text-ink">Дел пока нет</p>
          <p className="mt-1 text-[13px] text-muted">
            Дело появится, когда обращение возьмёт юрист.
          </p>
          <Link
            to="/chat"
            className="mt-4 inline-flex rounded-xl bg-ink-gradient px-5 py-2.5 text-sm font-semibold text-white"
          >
            Открыть чат
          </Link>
        </div>
      ) : (
        <div className="space-y-3">
          {cases.data?.map((item) => (
            <Link
              key={item.id}
              to={`/cases/${item.id}`}
              className="flex flex-wrap items-center gap-3 rounded-3xl border border-line bg-white/90 px-5 py-4 transition-colors hover:border-accent/40"
            >
              <div className="min-w-0 flex-1">
                <div className="truncate text-[16px] font-semibold text-ink">{item.title}</div>
                <div className="mt-1 text-[12.5px] text-muted">
                  {item.lawyerName ?? 'Юрист не назначен'} · срок{' '}
                  {dateLabel(item.responseDueAt)}
                </div>
              </div>
              <span
                className={`rounded-lg px-2.5 py-1 text-[12px] font-semibold ${STAGE_CLASS[item.stage]}`}
              >
                {STAGE_LABEL[item.stage]}
              </span>
              <span className="text-[13px] font-semibold text-accent">Открыть</span>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
}
